import { API_URL } from "../config/connect";

const getAuthHeaders = () => {
  const token = localStorage.getItem('google_id_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// ============================================
// 🔓 PUBLIC ROUTES (No Authentication Required)
// ============================================

// Countries - Public
export const getCountriesPublic = async () => {
  try {
    const response = await fetch(`${API_URL}/api/v1/location/countries`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching countries (public):', error);
    throw error;
  }
};

export const getCountryByIdPublic = async (countryId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/location/countries/${countryId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching country by ID (public):', error);
    throw error;
  }
};

// Regions - Public
export const getRegionsPublic = async (countryId = null) => {
  try {
    const url = countryId 
      ? `${API_URL}/api/v1/location/regions?country_id=${countryId}`
      : `${API_URL}/api/v1/location/regions`;
      
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching regions (public):', error);
    throw error;
  }
};

export const getRegionByIdPublic = async (regionId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/location/regions/${regionId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching region by ID (public):', error);
    throw error;
  }
};

// Districts - Public
export const getDistrictsPublic = async (regionId = null, countryId = null) => {
  try {
    let url = `${API_URL}/api/v1/location/districts`;
    const params = new URLSearchParams();
    if (regionId) params.append('region_id', regionId);
    if (countryId) params.append('country_id', countryId);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching districts (public):', error);
    throw error;
  }
};

export const getDistrictByIdPublic = async (districtId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/location/districts/${districtId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching district by ID (public):', error);
    throw error;
  }
};

// ============================================
// 🔐 ADMIN ROUTES (Authentication Required)
// ============================================

// Countries - Admin
export const getCountries = async () => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/countries`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching countries:', error);
    throw error;
  }
};

export const getCountryById = async (countryId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/countries/${countryId}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching country by ID:', error);
    throw error;
  }
};

export const createCountry = async (countryData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/countries`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(countryData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating country:', error);
    throw error;
  }
};

export const updateCountry = async (countryId, countryData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/countries/${countryId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(countryData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating country:', error);
    throw error;
  }
};

export const deleteCountry = async (countryId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/countries/${countryId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting country:', error);
    throw error;
  }
};

// Regions - Admin
export const getRegions = async (countryId = null) => {
  try {
    const url = countryId 
      ? `${API_URL}/api/v1/admin/regions?country_id=${countryId}`
      : `${API_URL}/api/v1/admin/regions`;
      
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching regions:', error);
    throw error;
  }
};

export const getRegionById = async (regionId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/regions/${regionId}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching region by ID:', error);
    throw error;
  }
};

export const createRegion = async (regionData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/regions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(regionData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating region:', error);
    throw error;
  }
};

export const updateRegion = async (regionId, regionData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/regions/${regionId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(regionData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating region:', error);
    throw error;
  }
};

export const deleteRegion = async (regionId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/regions/${regionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting region:', error);
    throw error;
  }
};

// Districts - Admin
export const getDistricts = async (regionId = null, countryId = null) => {
  try {
    let url = `${API_URL}/api/v1/admin/districts`;
    const params = new URLSearchParams();
    if (regionId) params.append('region_id', regionId);
    if (countryId) params.append('country_id', countryId);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching districts:', error);
    throw error;
  }
};

export const getDistrictById = async (districtId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/districts/${districtId}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching district by ID:', error);
    throw error;
  }
};

export const createDistrict = async (districtData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/districts`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(districtData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating district:', error);
    throw error;
  }
};

export const updateDistrict = async (districtId, districtData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/districts/${districtId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(districtData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating district:', error);
    throw error;
  }
};

export const deleteDistrict = async (districtId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/admin/districts/${districtId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting district:', error);
    throw error;
  }
};
